import {
  Button,
  OutlinedSelect,
  SearchBox,
  SelectOptionType,
  addToast,
  useMobile,
} from '@octano/global-ui';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { useActionKitImp } from '../../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../../locales/translations';
import { getSchoolsListAction } from '../actions';

export interface SearchFiltersValues {
  school: SelectOptionType | null;
  name: string | null;
  code: string | null;
}

export interface SearchFiltersOutput {
  schoolId?: number;
  name?: string;
  code?: string;
}

const defaultValues: SearchFiltersValues = {
  school: (null as unknown) as SelectOptionType,
  name: '',
  code: '',
};

interface SearchFiltersProps {
  onApply: (filters: SearchFiltersOutput) => void;
  canCreate?: boolean;
  onCreate?: () => void;
}

/**
 * Filtros del listado de Agrupaciones (HU 15564).
 *
 * Sigue el patrón de `views/academicOffers/courses/parts/SearchFilters.tsx`:
 * - 3 filtros en md=3 cada uno (Escuela / Nombre / Código)
 * - Cuarto col md=3 con botones Buscar + Limpiar (flex-fill, size="md")
 * - Botón "+ Crear" stacked debajo del par Buscar/Limpiar (opcional via canCreate)
 * - useMobile para el marginTop del col de botones
 * - i18n labels via cross-namespace `common:actions.search` / `common:actions.clean`
 */
export default function SearchFilters({
  onApply,
  canCreate,
  onCreate,
}: SearchFiltersProps): ReactElement {
  const prefix = 'list.filters';
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const isMobile = useMobile();
  const [schools, setSchools] = useState<SelectOptionType[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
  } = useForm<SearchFiltersValues>({ defaultValues });

  const { action: loadSchools } = useActionKitImp(getSchoolsListAction, {
    onSuccess: (resp) => {
      setSchools(
        (resp?.data ?? []).map((school) => ({
          value: school.id,
          label: school.name,
        })),
      );
    },
    onError: {
      unknown: () => {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t('errors.generic'),
        });
      },
    },
  });

  useEffect(() => {
    loadSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(
    (values: SearchFiltersValues) => {
      onApply({
        schoolId:
          values.school && values.school.value !== undefined
            ? Number(values.school.value)
            : undefined,
        name: values.name?.trim() ? values.name.trim() : undefined,
        code: values.code?.trim() ? values.code.trim() : undefined,
      });
    },
    [onApply],
  );

  const onSubmit = (values: SearchFiltersValues): void => {
    emit(values);
  };

  const handleClear = useCallback(() => {
    reset(defaultValues);
    emit(defaultValues);
  }, [reset, emit]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="school"
            options={schools}
            label={t(`${prefix}.school`)}
            placeholder={t(`${prefix}.schoolPlaceholder`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <SearchBox
            name="name"
            label={t(`${prefix}.name`)}
            placeholder={t(`${prefix}.namePlaceholder`)}
            control={control}
            clear={() => setValue('name', '')}
          />
        </Col>
        <Col xs={12} md={3}>
          <SearchBox
            name="code"
            label={t(`${prefix}.code`)}
            placeholder={t(`${prefix}.codePlaceholder`)}
            control={control}
            clear={() => setValue('code', '')}
          />
        </Col>
        <Col xs={12} md={3} style={{ marginTop: isMobile ? 0 : '1.5rem' }}>
          <div className="d-flex flex-column" style={{ gap: 24 }}>
            <div
              className="d-flex w-100 align-items-center h-100"
              style={{ gap: 8 }}
            >
              <Button
                type="submit"
                text={t('common:actions.search')}
                size="md"
                className="flex-fill"
              />
              <Button
                type="button"
                text={t('common:actions.clean')}
                size="md"
                outlined
                className="flex-fill"
                onClick={handleClear}
              />
            </div>
            {canCreate && onCreate && (
              <Button
                type="button"
                icon="plus"
                text={t('list.actions.create')}
                size="md"
                className="flex-fill"
                onClick={onCreate}
              />
            )}
          </div>
        </Col>
      </Row>
    </Form>
  );
}
